import { useQuery } from '@tanstack/react-query';

import { api } from '../api/cliente';
import type { Permiso, Rol } from '../api/tipos';
import { Aviso, Cargando, Encabezado, Etiqueta, Tarjeta } from '../ui/piezas';

/**
 * Los roles y lo que autoriza cada permiso.
 *
 * De solo lectura por ahora: editar el conjunto de permisos de un rol es la
 * operación más fácil de hacer mal en todo el sistema, y hoy la API no la
 * expone. Poder verlos ya resuelve la pregunta que se hace todo el mundo, que es
 * «¿por qué este asesor no ve tal cosa?».
 */
export default function Roles() {
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<Rol[]>('/admin/roles'),
  });
  const permisos = useQuery({
    queryKey: ['permisos'],
    queryFn: () => api<Permiso[]>('/admin/permisos'),
  });

  const porGrupo = (permisos.data ?? []).reduce<Record<string, Permiso[]>>((acumulado, p) => {
    (acumulado[p.grupo] ??= []).push(p);
    return acumulado;
  }, {});

  return (
    <>
      <Encabezado
        titulo="Roles y permisos"
        nota="El permiso dice qué se puede hacer; el alcance, sobre qué registros."
      />

      <Aviso error={roles.error ?? permisos.error} />

      <Tarjeta titulo="Roles">
        {roles.isPending ? (
          <Cargando />
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Rol</th>
                  <th>Alcance sugerido</th>
                  <th>Permisos</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(roles.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div>{r.nombre}</div>
                      <small style={{ opacity: 0.7 }}>{r.descripcion}</small>
                    </td>
                    <td>{r.alcanceSugerido.toLowerCase()}</td>
                    <td>
                      <small>{r.permisos.join(' · ')}</small>
                    </td>
                    <td>{r.esSistema && <Etiqueta>SISTEMA</Etiqueta>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="campo__ayuda" style={{ marginTop: 10 }}>
          Los roles del sistema no se borran ni se renombran: si alguien elimina ADMINISTRADOR,
          nadie vuelve a entrar.
        </p>
      </Tarjeta>

      <Tarjeta titulo="Qué autoriza cada permiso">
        {permisos.isPending ? (
          <Cargando />
        ) : (
          Object.entries(porGrupo).map(([grupo, lista]) => (
            <div key={grupo} style={{ marginBottom: 18 }}>
              <h3 style={{ marginBottom: 8 }}>{grupo}</h3>
              <div className="tabla-marco">
                <table>
                  <tbody>
                    {lista.map((p) => (
                      <tr key={p.codigo}>
                        <td style={{ width: 240 }}>
                          <code>{p.codigo}</code>
                        </td>
                        <td>{p.descripcion}</td>
                        <td style={{ width: 130 }}>
                          {p.usaAlcance && <Etiqueta>USA ALCANCE</Etiqueta>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </Tarjeta>
    </>
  );
}
